let editorDataUpdateId = 0

export const setEditorDataAndMoveCursorToEnd = (
  editor,
  nextValue,
  {
    shouldFocus = false,
    preserveSelection = false,
    selectionOffsetDelta = 0
  } = {}
) => {
  const selectionSnapshot = preserveSelection
    ? (() => {
        const selection = editor.model.document.selection
        const firstPosition = selection.getFirstPosition()

        if (!firstPosition) {
          return null
        }

        const path = [...firstPosition.path]
        const offsetIndex = path.length - 1
        path[offsetIndex] = Math.max(0, path[offsetIndex] + selectionOffsetDelta)

        return {
          path,
          isBackward: selection.isBackward
        }
      })()
    : null

  const readOnlyLockId = `set-editor-data-${++editorDataUpdateId}`
  editor.enableReadOnlyMode(readOnlyLockId)

  try {
    editor.setData(nextValue)
  } catch (error) {
    editor.disableReadOnlyMode(readOnlyLockId)
    throw error
  }

  window.requestAnimationFrame(() => {
    try {
      editor.model.change((writer) => {
        if (selectionSnapshot) {
          try {
            const root = editor.model.document.getRoot()
            const position = writer.createPositionFromPath(root, selectionSnapshot.path)
            writer.setSelection(position, { backward: selectionSnapshot.isBackward })
            return
          } catch (error) {
            // Fallback handled below when the old path no longer exists after setData.
          }
        }

        writer.setSelection(editor.model.document.getRoot(), 'end')
      })

      if (shouldFocus) {
        editor.editing.view.focus()
      } else {
        editor.ui.getEditableElement()?.blur()
      }
    } finally {
      editor.disableReadOnlyMode(readOnlyLockId)
    }
  })
}

export const limitHtmlToMaxCharacters = (html, maxCharacters) => {
  const parser = new window.DOMParser()
  const document = parser.parseFromString(html, 'text/html')

  const state = { remainingCharacters: maxCharacters }

  const isTableCellWithOnlyNbsp = (node) => {
    const parentTagName = node.parentElement?.tagName
    const isTableCell = parentTagName === 'TD' || parentTagName === 'TH'
    const value = node.nodeValue ?? ''
    return isTableCell && /^\u00A0+$/.test(value)
  }

  const trimNode = (parentNode) => {
    const childNodes = Array.from(parentNode.childNodes)

    for (const childNode of childNodes) {
      if (state.remainingCharacters <= 0) {
        childNode.remove()
        continue
      }

      if (childNode.nodeType === 3) {
        const currentValue = childNode.nodeValue ?? ''
        const isFillerNbsp = isTableCellWithOnlyNbsp(childNode)

        if (isFillerNbsp) {
          continue
        }

        if (currentValue.length > state.remainingCharacters) {
          childNode.nodeValue = currentValue.slice(0, state.remainingCharacters)
          state.remainingCharacters = 0
        } else {
          state.remainingCharacters -= currentValue.length
        }

        continue
      }

      if (childNode.nodeType === 1) {
        const element = childNode

        if (element.tagName === 'BR') {
          continue
        }

        trimNode(element)
      }
    }
  }

  trimNode(document.body)
  return document.body.innerHTML
}
